import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Archive, ArrowLeftRight, Copy, Network, Plus, RotateCcw, Settings, Trash2 } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import type { Tenant } from "@/api/generated/Tenant"
import { useSession } from "@/api/queries/session"
import { useSwitchTenant, useTenants } from "@/api/queries/tenants"
import { createColumns, DataTable } from "@/components/app/data-table"
import { PageContainer, PageHeader } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { EmptyState } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { TenantMark } from "@/components/shell/tenant-switcher"
import { Button } from "@/components/ui/button"
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import { CopySettingsDialog } from "@/features/studio/copy-settings-dialog"
import { tenantPermissions, useTenantActions } from "@/features/tenants/tenant-actions"
import { formatNumber } from "@/lib/format"
import { useUiStore } from "@/stores/ui"

export const Route = createFileRoute("/_app/settings/organizations")({
  staticData: { titleKey: "nav.items.organizations" },
  component: OrganizationsPage,
})

function OrganizationsPage() {
  const { t } = useTranslation()
  const session = useSession()
  const tenants = useTenants()
  const switchTenant = useSwitchTenant()
  const actions = useTenantActions()
  const navigate = useNavigate()
  const openDialog = useUiStore((s) => s.openDialog)
  const activeId = session.active_tenant?.id
  const isPlatformAdmin = !!session.user?.is_platform_admin
  const [copyTarget, setCopyTarget] = useState<number>()

  const columns = useMemo(
    () =>
      createColumns<Tenant>((c) => [
        c.accessor("name", {
          header: t("tenants.columns.name"),
          enableHiding: false,
          cell: ({ row }) => {
            const tenant = row.original
            return (
              <div className="flex min-w-0 items-center gap-2.5">
                <TenantMark
                  tenant={tenant}
                  className={tenant.archived_at ? "size-7 grayscale" : "size-7 text-xs"}
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 truncate font-medium">
                    {tenant.name}
                    {tenant.id === activeId && (
                      <StatusBadge tone="accent" dot>
                        {t("tenants.current")}
                      </StatusBadge>
                    )}
                  </div>
                  <code className="text-muted-foreground font-mono text-xs">{tenant.slug}</code>
                </div>
              </div>
            )
          },
        }),
        c.accessor("kind", {
          header: t("tenants.columns.kind"),
          cell: ({ getValue }) => (
            <span className="text-muted-foreground">
              {getValue() === "personal" ? t("tenants.kind.personal") : t("tenants.kind.organization")}
            </span>
          ),
        }),
        c.accessor((x) => x.role ?? "", {
          id: "role",
          header: t("tenants.columns.role"),
          meta: { label: t("tenants.columns.role") },
          cell: ({ getValue }) =>
            getValue() ? (
              <span>{t(`roles.${getValue() as "owner"}`)}</span>
            ) : (
              <span className="text-muted-foreground">{t("tenants.platformAccess")}</span>
            ),
        }),
        c.accessor("member_count", {
          header: t("tenants.columns.members"),
          meta: { align: "end" },
          cell: ({ getValue }) => <span className="tabular-nums">{formatNumber(getValue())}</span>,
        }),
        c.accessor((x) => (x.archived_at ? 1 : 0), {
          id: "status",
          header: t("tenants.columns.status"),
          meta: { label: t("tenants.columns.status") },
          cell: ({ row }) =>
            row.original.archived_at ? (
              <StatusBadge tone="warning">{t("tenants.deactivated")}</StatusBadge>
            ) : (
              <StatusBadge tone="success" dot>
                {t("status.active")}
              </StatusBadge>
            ),
        }),
        c.accessor("created_at", {
          header: t("tenants.columns.created"),
          sortFn: "datetime",
          cell: ({ getValue }) => <RelativeTime value={getValue()} className="text-muted-foreground" />,
        }),
      ]),
    [t, activeId],
  )

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t("tenants.title")}
        description={t("tenants.description")}
        icon={Network}
        actions={
          <Button onClick={() => openDialog("createTenant")} data-testid="create-organization">
            <Plus />
            {t("tenants.switcher.create")}
          </Button>
        }
      />
      <DataTable
        label={t("tenants.title")}
        data={tenants.data}
        columns={columns}
        getRowId={(x) => String(x.id)}
        loading={tenants.isPending}
        error={tenants.error}
        onRetry={() => void tenants.refetch()}
        searchable
        searchPlaceholder={t("tenants.search")}
        initialSorting={[{ id: "name", desc: false }]}
        isRowActive={(x) => x.id === activeId}
        onRowClick={(x) => {
          if (x.id === activeId) void navigate({ to: "/settings/organization" })
          else if (!x.archived_at) switchTenant.mutate({ id: x.id, name: x.name })
        }}
        rowActions={(x) => {
          const perms = tenantPermissions(x, isPlatformAdmin)
          const isActive = x.id === activeId
          return (
            <>
              {!isActive && !x.archived_at && (
                <DropdownMenuItem onClick={() => switchTenant.mutate({ id: x.id, name: x.name })}>
                  <ArrowLeftRight />
                  {t("tenants.switchTo")}
                </DropdownMenuItem>
              )}
              {isActive && (
                <DropdownMenuItem onClick={() => void navigate({ to: "/settings/organization" })}>
                  <Settings />
                  {t("tenants.switcher.settings")}
                </DropdownMenuItem>
              )}
              {!isActive && !x.archived_at && perms.canEdit && (
                <DropdownMenuItem onClick={() => setCopyTarget(x.id)}>
                  <Copy />
                  {t("studio.copy.rowAction")}
                </DropdownMenuItem>
              )}
              {x.archived_at ? (
                <DropdownMenuItem disabled={!perms.canEdit} onClick={() => void actions.reactivate(x)}>
                  <RotateCcw />
                  {t("tenants.reactivate.confirm")}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem disabled={!perms.canArchive} onClick={() => void actions.archive(x)}>
                  <Archive />
                  {t("tenants.archive.confirm")}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="destructive"
                disabled={!perms.canDelete}
                onClick={() => void actions.remove(x)}
              >
                <Trash2 />
                {t("tenants.delete.confirm")}
              </DropdownMenuItem>
            </>
          )
        }}
        emptyState={
          <EmptyState
            variant="plain"
            icon={Network}
            title={t("tenants.empty.title")}
            description={t("tenants.empty.description")}
            action={
              <Button size="sm" onClick={() => openDialog("createTenant")}>
                <Plus />
                {t("tenants.switcher.create")}
              </Button>
            }
          />
        }
      />
      <CopySettingsDialog
        open={copyTarget != null}
        onOpenChange={(o) => !o && setCopyTarget(undefined)}
        targetId={copyTarget}
      />
    </PageContainer>
  )
}
